import { motion } from 'framer-motion'
import { Layers, Factory, Globe, Quote } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'

const WhyInvestSection = () => {
  const reasons = [
    {
      icon: Layers,
      title: 'Unified Ecosystem',
      description: 'Integrating Senses, Brain, and Hands in one platform for complete industrial autonomy.',
      color: 'from-primary to-blue-600',
    },
    {
      icon: Factory,
      title: 'Industrial DNA',
      description: 'Products tested in Tunisia\'s most demanding environments, ensuring 99.9% reliability.',
      color: 'from-orange-500 to-red-500',
    },
    {
      icon: Globe,
      title: 'Regional Scalability',
      description: 'Bridge technology ready to scale across the MENA region\'s legacy machines.',
      color: 'from-green-500 to-emerald-500',
    },
  ]

  return (
    <section id="about" className="py-20 bg-secondary/50 relative overflow-hidden">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-accent/5 via-background to-background" />
      
      <div className="section-container relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-foreground mb-6">
            Why Invest in <span className="gradient-text">Dia Industries IoT?</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            The only local startup providing a complete "Connected Intelligence" ecosystem
          </p>
        </motion.div>

        {/* Reasons Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {reasons.map((reason, index) => (
            <motion.div
              key={reason.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              whileHover={{ y: -10, scale: 1.02 }}
            >
              <Card className="h-full hover:border-primary/30 transition-all duration-300">
                <CardContent className="p-8">
                  <div className={`w-16 h-16 bg-gradient-to-br ${reason.color} rounded-2xl flex items-center justify-center mb-6`}>
                    <reason.icon className="text-white" size={32} />
                  </div>
                  <h3 className="text-2xl font-semibold text-foreground mb-4">
                    {reason.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {reason.description}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Unified Ecosystem Visual */}
        <motion.div
          className="mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Card>
            <CardContent className="p-8 md:p-12">
              <div className="text-center mb-12">
                <h3 className="text-2xl md:text-3xl font-display font-bold text-foreground mb-4">
                  Complete Industrial Autonomy
                </h3>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  Our unified platform connects every aspect of industrial operations
                </p>
              </div>

              {/* Ecosystem Diagram */}
              <div className="relative aspect-video max-w-4xl mx-auto">
                {/* Center Hub */}
                <motion.div
                  className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3, type: 'spring', stiffness: 100 }}
                >
                  <div className="w-32 h-32 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center glow-effect">
                    <Layers className="text-white" size={48} />
                  </div>
                  <p className="text-foreground font-semibold text-center mt-4">Dia IoT Platform</p>
                </motion.div>

                {/* Orbiting Elements */}
                {[
                  { label: 'Sensors', icon: '📊', angle: 0 },
                  { label: 'AI Brain', icon: '🧠', angle: 72 },
                  { label: 'Robotics', icon: '🤖', angle: 144 },
                  { label: 'Analytics', icon: '📈', angle: 216 },
                  { label: 'Cloud', icon: '☁️', angle: 288 },
                ].map((item, index) => (
                  <motion.div
                    key={item.label}
                    className="absolute"
                    style={{
                      top: '50%',
                      left: '50%',
                    }}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                  >
                    <motion.div
                      className="flex flex-col items-center"
                      style={{
                        transform: `rotate(${item.angle}deg) translate(180px) rotate(-${item.angle}deg)`,
                      }}
                      animate={{
                        y: [0, -10, 0],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        delay: index * 0.3,
                      }}
                    >
                      <div className="w-16 h-16 bg-secondary border rounded-xl flex items-center justify-center text-2xl">
                        {item.icon}
                      </div>
                      <p className="text-muted-foreground text-sm mt-2 font-medium">{item.label}</p>
                    </motion.div>
                  </motion.div>
                ))}

                {/* Connection Rings */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 border border-border rounded-full" />
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 border border-border rounded-full" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Quote */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Card>
            <CardContent className="p-8 md:p-12">
              <div className="max-w-4xl mx-auto text-center">
                <Quote className="text-primary w-12 h-12 mx-auto mb-6" />
                <blockquote className="text-2xl md:text-3xl font-display font-medium text-foreground mb-6 leading-relaxed">
                  "Our 'Bridge Technology' allows us to scale by digitizing the legacy meters 
                  and machines already in use across the region."
                </blockquote>
                <div className="flex items-center justify-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center">
                    <Factory className="text-white" size={24} />
                  </div>
                  <div className="text-left">
                    <p className="text-foreground font-semibold">Dia Industries IoT</p>
                    <p className="text-muted-foreground text-sm">Industry 4.0 Pioneer</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  )
}

export default WhyInvestSection
